import { Box } from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';
import { useDropzone, type Accept, type FileError, type FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import { isExtensionAllowed } from '../../../_constants';

export type AoaDropableFile<TProps extends object> = TProps & {
  /**
   * The file that was dropped
   */
  file: File;
};

export interface AoaFileDropzoneDataQas {
  /**
   * The data-qa tag for the input
   */
  input: string;
  /**
   * The data-qa tag for the root element
   */
  root: string;
}

export interface AoaFileDropzoneProps<TProps extends object, TFile extends AoaDropableFile<TProps>> {
  /**
   * The current set of files that are already dropped, coming from Redux or Tanstack Store
   */
  currentFiles: TFile[];
  /**
   * A custom function that can perform extra checks when validating files.
   * This should return `true` if the file should be rejected or `false` if it should be accepted
   *
   * @param file - The file to be validated
   */
  customFileValidator?(file: File): boolean;
  /**
   * data-qa tags for testing
   */
  dataQas?: AoaFileDropzoneDataQas;
  /**
   * The regular expression that validates if the dropped file should be accepted or rejected
   */
  extensionRegex: RegExp;
  /**
   * The toast warning message to be shown when a file is rejection
   */
  extensionWarning: string;
  /**
   * The extension map to be passed to the dropzone component
   */
  extensions: Accept;
  /**
   * The children to show inside the box where files can be dropped
   */
  fileDropChildren?: ReactNode;
  /**
   * Whether the dropzone should be disabled or not
   */
  isDisabled?: boolean;
  /**
   * A maximum count of files that should be accepted, if exceeded no save action will be performed
   */
  maxUploadFiles?: number;
  /**
   * The toast warning message to be shown when the files to be uploaded exceeds the maximum,
   * required if {@link AoaFileDropzoneProps.maxUploadFiles | maxUploadFiles} is specified.
   */
  maxUploadSizeExceededWarning?: string;
  /**
   * Additional properties to set for every file that is to be saved
   */
  perFileProperties?(acceptedFile: File): TProps;
  /**
   * The function to overwrite the files in the client store.
   * This should overwrite all files, not merge with current, which is handled in this component
   *
   * @param files - The new set of files to be stored
   */
  storeFiles(files: TFile[]): void;
}

export function AoaFileDropzone<TProps extends object, TFile extends AoaDropableFile<TProps>>(
  props: Readonly<PropsWithChildren<AoaFileDropzoneProps<TProps, TFile>>>
) {
  function showMaxUploadFilesWarning() {
    toast(props.maxUploadSizeExceededWarning ?? `U heeft meer dan ${props.maxUploadFiles} bestand(en) geselecteerd. Dit is niet toegestaan.`, {
      type: 'warning'
    });
  }

  function handleDrop(acceptedFiles: File[], fileRejections: FileRejection[]) {
    if (fileRejections.length > 0) {
      if (fileRejections.every((file) => file.errors.every((error) => error.code === 'too-many-files'))) {
        showMaxUploadFilesWarning();
      } else {
        toast(props.extensionWarning, { type: 'warning' });
      }
    }

    const filesClone = [...props.currentFiles];
    for (const acceptedFile of acceptedFiles) {
      const existingFileIndex = filesClone.findIndex((file) => file.file.name === acceptedFile.name);
      if (existingFileIndex === -1) {
        filesClone.push({ file: acceptedFile, ...props.perFileProperties?.(acceptedFile) } as TFile & TProps);
      } else {
        filesClone.splice(existingFileIndex, 1, {
          file: acceptedFile,
          ...props.perFileProperties?.(acceptedFile)
        } as TFile & TProps);
      }
    }

    if (props.maxUploadFiles && filesClone.length > props.maxUploadFiles) {
      showMaxUploadFilesWarning();
    } else {
      props.storeFiles(filesClone);
    }
  }

  function fileValidator(file: File): FileError | null {
    if (props.customFileValidator?.(file)) {
      return {
        code: 'custom-validator',
        message: props.extensionWarning
      };
    }

    if (isExtensionAllowed(props.extensionRegex, file.name)) {
      return null;
    }

    return {
      code: 'name-not-ok',
      message: props.extensionWarning
    };
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    disabled: props.isDisabled,
    accept: props.extensions,
    maxFiles: props.maxUploadFiles,
    validator: fileValidator
  });

  return (
    <Box
      {...getRootProps()}
      data-qa={props.dataQas?.root ?? 'file-dropzone-root'}
      sx={{
        '&:focus': {
          outline: 'none'
        }
      }}
    >
      <input {...getInputProps()} aria-label='File drop' data-qa={props.dataQas?.input ?? 'file-drop-input'} data-testid='file-drop-input' />
      <Box
        sx={{
          backgroundColor: 'var(--color-rijks-grey-2)',
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: 'black',
          marginBottom: 0.5,
          textAlign: 'center'
        }}
      >
        {props.fileDropChildren}
      </Box>
    </Box>
  );
}
